<template>
  <b-overlay :show="loading">
    <b-alert
      variant="danger"
      :show="consultationData === undefined && !loading"
    >
      <h4 class="alert-heading">
        Error fetching Consultation Data
      </h4>

      <div class="alert-body">
        <b-link
          class="alert-link"
          :to="{ name: 'admin-consultations'}"
        >
          Consultations
        </b-link>
      </div>
    </b-alert>

    <template v-if="consultationData && patientData && doctorData">
      <div>
        <b-row class="d-flex mt-0 justify-content-end">
          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanCancel')"
              type="submit"
              class="mb-1 mx-2"
              variant="outline-danger" 
              style="width: 220px"
              :disabled="loading"
              @click="onCancel"
            >
              <feather-icon icon="XIcon" />
              <span class="align-middle ml-50">Cancel</span>
            </b-button>
          </b-col>

          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanUpdate')"
              type="primary"
              class="mb-1 mx-2"
              variant="outline-primary" 
              style="width: 220px"
              :disabled="loading"
              @click="onViewEdit(consultationData.id)"
            >
              <feather-icon icon="PenToolIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-button>
          </b-col>

          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanMarkAsCompleted')"
              variant="primary"
              type="submit"
              class="mb-1 mx-2"
              style="width: 220px"
              :disabled="loading"
              @click="onComplete"
            >
              <feather-icon icon="CheckIcon" />
              <span class="align-middle ml-50">Mark as completed</span>
            </b-button>
          </b-col>
          
          <b-button
            v-if="consultationData.status === 'ongoing'"
            variant="outline-primary"
            block
            type="submit"
            class="mb-5 mx-2"
            style="width: 220px"
            :disabled="loading"
            @click="showMedicalHistory"
          >
            <feather-icon icon="EyeIcon" />
            <span class="align-middle ml-50">Medical History</span>
          </b-button>

          <b-button
            v-if="(consultationData.status === 'cancelled' || consultationData.status === 'completed') && can('consultations:CanReopen')"
            block
            type="submit"
            class="mb-5 mx-2"
            variant="info" 
            style="width: 220px"
            :disabled="loading"
            @click="onReopen"
          >
            <feather-icon icon="Edit2Icon" />
            <span class="align-middle ml-50">Re-open consultation</span>
          </b-button>
        </b-row>

        <b-row class="match-height">
          <b-col cols="12" md="12">
            <b-row class="d-flex details-wrapper align-items-center">
              <b-col cols="12" md="4">
                <p>
                  Patient Name: {{ patientData.full_name }}
                </p>
              </b-col>
              <b-col cols="12" md="4">
                <p>
                  Insurance Number: {{ patientData.policy_number }}
                </p>
              </b-col>
              <b-col cols="12" md="4">
                <p>
                  Attending Physician: {{ doctorData.full_name }}
                </p>
              </b-col>
            </b-row>

            <b-row class="match-height mt-2">
              <b-col cols="12" md="4">
                <presenting-complaints-status 
                  :consultation-data="consultationData" 
                  @refresh="onRefreshRef('directQuestions')"
                />
              </b-col>

              <b-col cols="12" md="4">
                <presenting-complaint-history-status 
                  :consultation-data="consultationData" 
                />
              </b-col>

              <b-col cols="12" md="4">
                <doctors-note-status
                  :consultation-data="consultationData" 
                />
              </b-col> 
            </b-row>

            <b-row class="match-height mt-2">
              <b-col cols="12" md="4">
                <patient-diagnosis-status
                  :consultation-data="consultationData" 
                />
              </b-col>

              <b-col cols="12" md="8">
                <prescription-status
                  :consultation-data="consultationData" 
                />
              </b-col>
            </b-row>

            <b-row class="match-height mt-2">
              <b-col cols="12" md="4">
                <treatment-advice-status
                  :consultation-data="consultationData" 
                  @refresh="fetchConsultationData"
                />
              </b-col>

              <b-col cols="12" md="8">
                <lab-request-status
                  :consultation-data="consultationData" 
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <medical-history-sidebar
          :patient-data="patientData"
          :is-medical-history-sidebar-active.sync="isMedicalHistorySidebarActive"
        />
      </div>
    </template>
  </b-overlay>
</template>

<script>
import { get } from "lodash";
import { BAlert, BAvatar, BBadge, BImg, BCard, BCol, BLink, BOverlay, BRow, BCardImg, BButton } from "bootstrap-vue"

import VueEasyLightbox from 'vue-easy-lightbox';
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import LabRequestStatus from "@core/components/shared/admin/consultation/LabRequestStatus.vue";
import DoctorsNoteStatus from '@core/components/shared/admin/consultation/DoctorsNoteStatus.vue';
import PrescriptionStatus from "@core/components/shared/admin/consultation/PrescriptionStatus.vue";
import MedicalHistorySidebar from "@core/components/shared/admin/consultation/MedicalHistorySidebar.vue"
import TreatmentAdviceStatus from "@core/components/shared/admin/consultation/TreatmentAdviceStatus.vue";
import PatientDiagnosisStatus from "@core/components/shared/admin/consultation/PatientDiagnosisStatus.vue";
import PresentingComplaintsStatus from "@core/components/shared/admin/consultation/PresentingComplaintsStatus.vue";
import PresentingComplaintHistoryStatus from "@/@core/components/shared/admin/consultation/PresentingComplaintHistoryStatus.vue";


export default {
  components: {
    ListTable,
    VueEasyLightbox,
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    BButton,
    // components
    DoctorsNoteStatus,
    PrescriptionStatus,
    MedicalHistorySidebar,
    TreatmentAdviceStatus,
    PatientDiagnosisStatus,
    PresentingComplaintsStatus,
    PresentingComplaintHistoryStatus,

    LabRequestStatus,
},
  directives: {},
  data(){
    return {
      loading: false,

      doctorData: null,
      patientData: null,
      consultationData: null,
      isMedicalHistorySidebarActive: false,
    }
  },
  created() {
    this.fetchConsultationData();
  },
  methods: {
    async fetchConsultationData(){
      try {
        this.loading = true;
        const { id } = this.$route.params;

        const response = await this.useJwt().mainService.consultations.fetch(id);
        this.consultationData = get(response, 'data.data');

        await this.fetchPatient()
        await this.fetchDoctor()
      } catch (error){
        this.consultationData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchPatient(){
      try {
        this.loading = true;
        const { patient_id } = this.consultationData

        const response = await this.useJwt().mainService.patients.fetch(patient_id);
        this.patientData = get(response, 'data.data')
      } catch (error){
        this.patientData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchDoctor(){
      try {
        this.loading = true;
        const { created_by } = this.consultationData

        const doctor_response = await this.useJwt().mainService.users.fetch(created_by)
        this.doctorData = get(doctor_response, 'data.data');
      } catch (error){
        this.consultationData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onCancel(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.consultations.cancel(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation cancelled successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onReopen(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.consultations.reopen(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation reopened successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onComplete(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanMarkAsCompleted`)) {
          return
        }

        await this.useJwt().mainService.consultations.complete(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation marked as completed successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    onRefreshRef(ref){
      if (this.$refs[ref]){
        this.$refs[ref].fetchAllData();
      }
    },
    onViewEdit(consultation_id){
      this.$router.push({
        name: 'admin-medical-consultation-edit',
        params: { id: consultation_id }
      })
    },
    showMedicalHistory(){
      this.isMedicalHistorySidebarActive = !this.isMedicalHistorySidebarActive;
    }
  }
}
</script>

<style lang="scss">
  small {
    font-size: 10px;
  }

  .details-wrapper {
    background-color: #EAF1F9;
    padding: 22px 4px 12px 20px;
    margin-left: 3px;
    margin-right: 3px;
  }

  .details-wrapper div p {
    font-size: 16px
  }
</style>
